/**
 * useAuth — CARLOS FELIPE
 *
 * Sistema de autenticação 100% próprio.
 * Não usa Lumi Auth. Não precisa de OTP.
 * Guarda o token em localStorage e verifica-o contra a BD via Deno Function.
 */
import { useEffect, useState, useCallback } from 'react'
import { lumi } from '@/lib/lumi'

export interface AuthUser {
  id:           string
  userId:       string
  username:     string
  displayName:  string
  permission:   string
  email:        string
  isBlocked?:   boolean
  blockMessage?: string
}

const TOKEN_KEY = 'rcf_session_token'

// ─── Funções utilitárias de sessão ────────────────────────────────────────────

export function getStoredToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY) } catch { return null }
}

export function storeToken(token: string): void {
  try { localStorage.setItem(TOKEN_KEY, token) } catch { /* ignorado */ }
}

export function clearToken(): void {
  try { localStorage.removeItem(TOKEN_KEY) } catch { /* ignorado */ }
}

// ─── Login: chama a Deno Function com action="login" ─────────────────────────

export async function signIn(username: string, password: string): Promise<AuthUser> {
  const result = await lumi.functions.invoke('createAdminUser', {
    method: 'POST',
    body: { action: 'login', username: username.trim(), password },
  }) as any

  if (!result?.success) {
    throw new Error(result?.error ?? 'Utilizador ou senha incorretos.')
  }

  storeToken(result.token)
  return result.user as AuthUser
}

// ─── Logout ──────────────────────────────────────────────────────────────────

export function signOut(): void {
  clearToken()
  window.location.href = '/admin/login'
}

// ─── Hook principal ───────────────────────────────────────────────────────────

export function useAuth() {
  const [isLoading, setIsLoading]             = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser]                       = useState<AuthUser | null>(null)

  const isAdmin = user?.permission === 'admin'

  const verifySession = useCallback(async () => {
    const token = getStoredToken()
    if (!token) {
      setIsLoading(false)
      return
    }

    const onAdminRoute = window.location.pathname.startsWith('/admin')

    // Na página pública não chamamos a função Deno: o SDK mostra um toast
    // vermelho em qualquer falha de rede. O token local basta para o ícone do header.
    if (!onAdminRoute) {
      setIsAuthenticated(true)
      setIsLoading(false)
      return
    }

    try {
      const result = await lumi.functions.invoke('createAdminUser', {
        method: 'POST',
        body: { action: 'verify', token },
      }) as any

      if (result?.valid && result?.user) {
        setUser(result.user as AuthUser)
        setIsAuthenticated(true)
      } else {
        clearToken()
        setIsAuthenticated(false)
        setUser(null)
      }
    } catch {
      setIsAuthenticated(false)
      setUser(null)
      clearToken()
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    verifySession()
  }, [verifySession])

  return { user, isAuthenticated, isLoading, isAdmin }
}
